import { NEWSLETTER_DISCOUNT_CODE, NEWSLETTER_DISCOUNT_PERCENT, NEWSLETTER_PROMO_ID } from "@/config/newsletterDiscount";

export type PromoTheme = 
  | 'chaos'      // Red/Yellow/Purple gradient (ChaosKitchen brand)
  | 'fire'       // Red/Orange hot theme
  | 'cool'       // Blue/Purple cool theme
  | 'gold'       // Gold/Yellow premium theme
  | 'green'      // Green/teal eco/fresh theme
  | 'valentine'  // Pink/Rose romantic theme
  | 'custom';    // Custom colors

export interface PromoColors {
  background: string;
  text: string;
  gradient: string;
  accent: string;
  buttonBg: string;
  buttonText: string;
}

export interface Promotion {
  id: string;
  name: string;
  code: string;
  creditsCode?: string;
  subscriptionCode?: string;
  discountPercent: number;
  creditsDiscountPercent?: number;
  subscriptionDiscountPercent?: number;
  startDate: Date;
  endDate: Date;
  appliesTo: 'subscriptions' | 'credits' | 'all';
  autoApply: boolean;
  theme: PromoTheme;
  customColors?: PromoColors;
  localStorageKey: string;
  i18nKey: string;
}

const themeColors: Record<Exclude<PromoTheme, 'custom'>, PromoColors> = {
  chaos: {
    background: 'bg-black',
    text: 'text-white',
    gradient: 'bg-gradient-to-r from-chaos-red/20 via-chaos-yellow/20 to-chaos-purple/20',
    accent: 'bg-gradient-to-r from-chaos-red via-chaos-yellow to-chaos-purple bg-clip-text text-transparent',
    buttonBg: 'bg-white',
    buttonText: 'text-black',
  },
  fire: {
    background: 'bg-gradient-to-r from-red-900 to-orange-800',
    text: 'text-white',
    gradient: 'bg-gradient-to-r from-red-500/20 via-orange-500/20 to-yellow-500/20',
    accent: 'bg-gradient-to-r from-orange-400 via-red-400 to-yellow-400 bg-clip-text text-transparent',
    buttonBg: 'bg-orange-500',
    buttonText: 'text-white',
  },
  cool: {
    background: 'bg-gradient-to-r from-blue-900 to-purple-900',
    text: 'text-white',
    gradient: 'bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-indigo-500/20',
    accent: 'bg-gradient-to-r from-blue-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent',
    buttonBg: 'bg-blue-500',
    buttonText: 'text-white',
  },
  gold: {
    background: 'bg-gradient-to-r from-yellow-900 to-amber-800',
    text: 'text-white',
    gradient: 'bg-gradient-to-r from-yellow-500/20 via-amber-500/20 to-orange-500/20',
    accent: 'bg-gradient-to-r from-yellow-300 via-amber-400 to-orange-400 bg-clip-text text-transparent',
    buttonBg: 'bg-yellow-500',
    buttonText: 'text-black',
  },
  green: {
    background: 'bg-gradient-to-r from-green-900 to-teal-800',
    text: 'text-white',
    gradient: 'bg-gradient-to-r from-green-500/20 via-teal-500/20 to-emerald-500/20',
    accent: 'bg-gradient-to-r from-green-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent',
    buttonBg: 'bg-green-700',
    buttonText: 'text-white',
  },
  valentine: {
    background: 'bg-gradient-to-r from-pink-900 to-rose-800',
    text: 'text-white',
    gradient: 'bg-gradient-to-r from-pink-500/20 via-rose-500/20 to-red-500/20',
    accent: 'bg-gradient-to-r from-pink-400 via-rose-400 to-red-400 bg-clip-text text-transparent',
    buttonBg: 'bg-pink-500',
    buttonText: 'text-white',
  },
};

export const getPromoColors = (promo: Promotion): PromoColors => {
  if (promo.theme === 'custom' && promo.customColors) {
    return promo.customColors;
  }
  return themeColors[promo.theme as Exclude<PromoTheme, 'custom'>] || themeColors.chaos;
};

export const PROMOTIONS: Promotion[] = [
  {
    id: NEWSLETTER_PROMO_ID,
    name: 'Newsletter welcome',
    code: NEWSLETTER_DISCOUNT_CODE,
    creditsCode: NEWSLETTER_DISCOUNT_CODE,
    subscriptionCode: NEWSLETTER_DISCOUNT_CODE,
    discountPercent: NEWSLETTER_DISCOUNT_PERCENT,
    creditsDiscountPercent: NEWSLETTER_DISCOUNT_PERCENT,
    subscriptionDiscountPercent: NEWSLETTER_DISCOUNT_PERCENT,
    startDate: new Date('2026-08-19T00:00:00'),
    endDate: new Date('2099-12-31T23:59:59'),
    appliesTo: 'all',
    autoApply: false,
    theme: 'green',
    localStorageKey: 'newsletterWelcomeBannerDismissed',
    i18nKey: 'newsletterWelcome',
  },
  {
    id: 'obs-plugin-launch-2026',
    name: 'OBS Companion Launch',
    code: 'OBSPLUGIN50',
    subscriptionCode: 'OBSPLUGIN50',
    discountPercent: 50,
    subscriptionDiscountPercent: 50,
    startDate: new Date('2026-08-10T00:00:00'),
    endDate: new Date('2026-08-24T23:59:59'),
    appliesTo: 'subscriptions',
    autoApply: true,
    theme: 'chaos',
    localStorageKey: 'obsPluginLaunchBannerDismissed',
    i18nKey: 'obsPluginLaunch',
  },
  {
    id: 'winter-madness-2025',
    name: 'Winter Madness 2025',
    code: 'WINTERMADNESS',
    creditsCode: 'WINTERMADNESS40',
    subscriptionCode: 'WINTERMADNESS50',
    discountPercent: 40,
    creditsDiscountPercent: 40,
    subscriptionDiscountPercent: 50,
    startDate: new Date('2025-12-19T00:00:00'),
    endDate: new Date('2025-12-25T23:59:59'),
    appliesTo: 'all',
    autoApply: true,
    theme: 'fire',
    localStorageKey: 'winterMadnessBannerDismissed',
    i18nKey: 'winterMadness',
  },
  {
    id: 'new-year-2025',
    name: 'New Year Sale 2025',
    code: 'NEWYEARMORECHAOS',
    creditsCode: 'NEWYEARMORECHAOS25',
    subscriptionCode: 'NEWYEARMORECHAOS30',
    discountPercent: 25,
    creditsDiscountPercent: 25,
    subscriptionDiscountPercent: 30,
    startDate: new Date('2025-12-26T00:00:00'),
    endDate: new Date('2026-01-02T23:59:59'),
    appliesTo: 'all',
    autoApply: true,
    theme: 'gold',
    localStorageKey: 'newYearSaleBannerDismissed',
    i18nKey: 'newYearSale',
  },
  {
    id: 'valentines-week',
    name: "Valentine's Week",
    code: 'VALENTINESCHAOS50',
    discountPercent: 50,
    subscriptionDiscountPercent: 50,
    startDate: new Date('2026-02-14T00:00:00'),
    endDate: new Date('2026-02-21T23:59:59'),
    appliesTo: 'subscriptions',
    autoApply: true,
    theme: 'valentine',
    localStorageKey: 'valentinesSaleBannerDismissed',
    i18nKey: 'valentinesSale',
  },
  {
    id: 'music-launch-2026',
    name: 'Music promo music LAUNCH v1',
    code: 'MUSICLAUNCH25',
    discountPercent: 25,
    creditsDiscountPercent: 25,
    subscriptionDiscountPercent: 25,
    startDate: new Date('2026-03-02T00:00:00'),
    endDate: new Date('2026-03-31T23:59:59'),
    appliesTo: 'all',
    autoApply: true,
    theme: 'green',
    localStorageKey: 'musicLaunchBannerDismissed',
    i18nKey: 'recipeBookLaunch',
  },
  {
    id: 'merch-discount-2026',
    name: 'Merch 25% Off',
    code: 'MERCH25',
    discountPercent: 25,
    startDate: new Date('2026-03-02T00:00:00'),
    endDate: new Date('2026-03-31T23:59:59'),
    appliesTo: 'all',
    autoApply: false,
    theme: 'gold',
    localStorageKey: 'merchDiscountBannerDismissed',
    i18nKey: 'merchDiscount',
  },
  {
    id: 'recipe-book-discount-2026',
    name: 'Recipe Book Discount',
    code: 'RECIPE25',
    discountPercent: 25,
    startDate: new Date('2026-03-02T00:00:00'),
    endDate: new Date('2026-03-31T23:59:59'),
    appliesTo: 'all',
    autoApply: true,
    theme: 'green',
    localStorageKey: 'recipeBookDiscountBannerDismissed',
    i18nKey: 'recipeBookDiscount',
  },
];

export const getDiscountForType = (
  promo: Promotion, 
  type: 'subscriptions' | 'credits'
): number => {
  if (type === 'credits' && promo.creditsDiscountPercent !== undefined) {
    return promo.creditsDiscountPercent;
  }
  if (type === 'subscriptions' && promo.subscriptionDiscountPercent !== undefined) {
    return promo.subscriptionDiscountPercent;
  }
  return promo.discountPercent;
};

function withYearlyWindows(promos: Promotion[]): Promotion[] {
  const year = new Date().getFullYear();
  return promos.map((promo) => {
    if (promo.id !== "valentines-week") return promo;
    return {
      ...promo,
      startDate: new Date(year, 1, 14, 0, 0, 0),
      endDate: new Date(year, 1, 21, 23, 59, 59),
    };
  });
}

export const getActivePromotion = (): Promotion | null => {
  const now = new Date();
  return withYearlyWindows(PROMOTIONS).find(
    promo =>
      promo.autoApply &&
      now >= promo.startDate &&
      now <= promo.endDate
  ) || null;
};

export const getPromotionByCode = (code: string): Promotion | null => {
  const upperCode = code.toUpperCase();
  return withYearlyWindows(PROMOTIONS).find(promo => 
    promo.code === upperCode || 
    promo.creditsCode === upperCode || 
    promo.subscriptionCode === upperCode
  ) || null;
};

export const isPromotionActive = (promoId: string): boolean => {
  const promo = withYearlyWindows(PROMOTIONS).find(p => p.id === promoId || (promoId === 'valentines-2026' && p.id === 'valentines-week'));
  if (!promo) return false;
  const now = new Date();
  return now >= promo.startDate && now <= promo.endDate;
};

export const getAutoApplyPromotion = (type: 'subscriptions' | 'credits'): Promotion | null => {
  const now = new Date();
  return withYearlyWindows(PROMOTIONS).find(
    promo => 
      promo.autoApply && 
      now >= promo.startDate && 
      now <= promo.endDate &&
      (promo.appliesTo === type || promo.appliesTo === 'all')
  ) || null;
};

/**
 * A promo is only safe to display as a discounted price if checkout can actually
 * apply it (it needs a code for that product type). This keeps the UI honest for
 * any future promo added to PROMOTIONS, without per-campaign code changes.
 */
export const getDisplayablePromotion = (
  type: 'subscriptions' | 'credits'
): Promotion | null => {
  const promo = getAutoApplyPromotion(type);
  if (!promo) return null;
  const code =
    type === 'credits'
      ? promo.creditsCode || promo.code
      : promo.subscriptionCode || promo.code;
  if (!code) return null;
  if (getDiscountForType(promo, type) <= 0) return null;
  return promo;
};

/** Next moment the displayed discount for `type` could change (start or end of a window). */
export const getNextPromoBoundary = (
  type: 'subscriptions' | 'credits'
): Date | null => {
  const now = new Date();
  const relevant = withYearlyWindows(PROMOTIONS).filter(
    (promo) =>
      promo.autoApply && (promo.appliesTo === type || promo.appliesTo === 'all')
  );
  const upcoming = relevant
    .flatMap((promo) => [promo.startDate, promo.endDate])
    .filter((date) => date.getTime() > now.getTime())
    .sort((a, b) => a.getTime() - b.getTime());
  return upcoming[0] || null;
};
